/**
 * Wizard Types
 *
 * Types for the TurboTax-style guided permit intake wizard.
 * Supports 6-step interview flow with database-driven options.
 */

import type {
  ProjectCategory,
  PropertyType,
  ProjectAddress,
  ProjectClient,
  ProjectTradeScope,
  DevelopmentProjectType,
} from './project';

// Wizard mode - determines which flow the user is following
export type WizardMode = 'single_permit' | 'development_project';

// Wizard step identifiers (includes both single-permit and development steps)
export type WizardStep =
  | 'entry'           // NEW: What are you working on?
  | 'project_type'    // NEW (dev only): Development type selection
  | 'project_basics'  // NEW (dev only): Project name, address
  | 'units'           // NEW (dev only): Unit setup
  | 'basics'          // Property type, address (single-permit)
  | 'trades'          // Trade selection
  | 'scopes'          // Scope discovery for each trade
  | 'permits'         // Permit determination
  | 'details'         // Conditional requirements & details
  | 'review';         // Review & create

// Step configuration
export interface WizardStepConfig {
  id: WizardStep;
  number: number;
  title: string;
  description: string;
}

// Steps for single-permit mode (7 steps)
export const SINGLE_PERMIT_STEPS: WizardStepConfig[] = [
  { id: 'entry', number: 1, title: 'Getting Started', description: 'What are you working on?' },
  { id: 'basics', number: 2, title: 'Project Basics', description: 'What type of project is this?' },
  { id: 'trades', number: 3, title: 'Trade Selection', description: 'What work will be performed?' },
  { id: 'scopes', number: 4, title: 'Scope Discovery', description: 'Tell us more about the work' },
  { id: 'permits', number: 5, title: 'Permits Needed', description: 'Based on your project, you\'ll need...' },
  { id: 'details', number: 6, title: 'Project Details', description: 'A few more details for compliance' },
  { id: 'review', number: 7, title: 'Review & Create', description: 'Ready to create your permit applications' },
];

// Steps for development project mode (9 steps)
export const DEVELOPMENT_PROJECT_STEPS: WizardStepConfig[] = [
  { id: 'entry', number: 1, title: 'Getting Started', description: 'What are you working on?' },
  { id: 'project_type', number: 2, title: 'Project Type', description: 'What kind of development?' },
  { id: 'project_basics', number: 3, title: 'Project Info', description: 'Name and location' },
  { id: 'units', number: 4, title: 'Units', description: 'Define units/lots' },
  { id: 'trades', number: 5, title: 'Trade Selection', description: 'What work will be done?' },
  { id: 'scopes', number: 6, title: 'Scope Discovery', description: 'Tell us more about the work' },
  { id: 'permits', number: 7, title: 'Permits Needed', description: 'Required permits' },
  { id: 'details', number: 8, title: 'Project Details', description: 'Additional details' },
  { id: 'review', number: 9, title: 'Review & Create', description: 'Confirm and create' },
];

// Get steps based on mode
export function getWizardSteps(mode: WizardMode): WizardStepConfig[] {
  return mode === 'development_project' ? DEVELOPMENT_PROJECT_STEPS : SINGLE_PERMIT_STEPS;
}

// Legacy: Default steps (single-permit mode without entry step for backward compat)
export const WIZARD_STEPS: WizardStepConfig[] = [
  { id: 'basics', number: 1, title: 'Project Basics', description: 'What type of project is this?' },
  { id: 'trades', number: 2, title: 'Trade Selection', description: 'What work will be performed?' },
  { id: 'scopes', number: 3, title: 'Scope Discovery', description: 'Tell us more about the work' },
  { id: 'permits', number: 4, title: 'Permits Needed', description: 'Based on your project, you\'ll need...' },
  { id: 'details', number: 5, title: 'Project Details', description: 'A few more details for compliance' },
  { id: 'review', number: 6, title: 'Review & Create', description: 'Ready to create your permit applications' },
];

// Confidence levels for auto-filled values
export type ConfidenceLevel = 'high' | 'medium' | 'low';

// Contractor role types for permit responsibility
export type ContractorRole =
  | 'gc_all_permits'      // GC pulling all permits (Master + Sub-permits)
  | 'gc_building_only'    // GC pulling Building permit only, subs pull trade permits
  | 'specialty_prime'     // Specialty contractor as prime (e.g., roofer for re-roof)
  | 'owner_builder';      // Owner-Builder (homeowner acting as own contractor)

// Contractor role configuration
export interface ContractorRoleConfig {
  code: ContractorRole;
  name: string;
  description: string;
  permitResponsibility: string;
  liabilityNotes: string;
  typicalScenarios: string[];
}

// Available contractor roles (matches database contractor_roles table)
export const CONTRACTOR_ROLES: ContractorRoleConfig[] = [
  {
    code: 'gc_all_permits',
    name: 'GC Pulling All Permits',
    description: 'General Contractor pulls Building (Master) permit and all trade Sub-permits',
    permitResponsibility: 'GC pulls Building permit as Master, plus all required Sub-permits (Electrical, Plumbing, etc.)',
    liabilityNotes: 'Full project liability rests with GC. All inspections coordinated by GC.',
    typicalScenarios: [
      'New construction',
      'Major remodels',
      'Commercial build-outs',
      'Multi-trade residential projects',
    ],
  },
  {
    code: 'gc_building_only',
    name: 'GC Pulling Building Only',
    description: 'GC pulls Building permit; licensed subcontractors pull their own trade permits',
    permitResponsibility: 'GC pulls Building permit. Each licensed sub pulls their own trade permit (Electrical, Plumbing, HVAC, etc.)',
    liabilityNotes: 'GC responsible for structural/building work. Each sub responsible for their trade permit/inspection.',
    typicalScenarios: [
      'Large custom homes',
      'Commercial projects with specialized subs',
      'Projects where subs prefer to pull own permits',
    ],
  },
  {
    code: 'specialty_prime',
    name: 'Specialty Contractor as Prime',
    description: 'Single-trade project where specialty contractor is the permit holder',
    permitResponsibility: 'Specialty contractor (Roofer, Electrician, Plumber, etc.) pulls permit directly. No Building permit needed.',
    liabilityNotes: 'Single trade contractor holds full responsibility for their permitted work.',
    typicalScenarios: [
      'Roof replacement (no structural changes)',
      'HVAC changeout',
      'Panel upgrade',
      'Water heater replacement',
      'Re-pipe projects',
    ],
  },
  {
    code: 'owner_builder',
    name: 'Owner-Builder',
    description: 'Homeowner acts as their own general contractor for personal residence',
    permitResponsibility: 'Owner pulls Building permit as owner-builder. Licensed subs required for Electrical, Plumbing, HVAC, Gas.',
    liabilityNotes: 'Owner personally liable. Cannot sell property for 1 year without disclosure. Must sign affidavit.',
    typicalScenarios: [
      'Homeowner remodel of personal residence',
      'Homeowner building own home',
      'DIY projects requiring permits',
    ],
  },
];

// Auto-filled value with confidence tracking
export interface AutoFilledValue<T> {
  value: T;
  confidence: ConfidenceLevel;
  source: string;           // Description of where the value came from
  userOverridden?: boolean; // True if user changed the auto-filled value
}

// Step 1: Basics data
export interface WizardBasicsData {
  projectName?: string;
  category?: ProjectCategory;
  propertyType?: PropertyType;
  address?: ProjectAddress;
  jurisdictionId?: number;
  jurisdictionCode?: string;
  jurisdictionName?: string;
  isHvhz?: boolean;         // High Velocity Hurricane Zone
  isFloodZone?: boolean;
  isCoastalZone?: boolean;
  // Contractor role determines permit pulling responsibility
  contractorRole?: ContractorRole;
}

// Step 2: Trades data
export interface WizardTradesData {
  selectedTradeIds: number[];
  trades: Array<{
    id: number;
    code: string;
    name: string;
  }>;
}

// Individual scope selection
export interface SelectedScope {
  scopeId: number;
  scopeCode: string;
  scopeName: string;
}

// Step 3: Scopes data - supports multiple scopes per trade
export interface WizardScopesData {
  tradeScopes: Array<{
    tradeId: number;
    tradeCode: string;
    tradeName: string;
    selectedScopes: SelectedScope[];  // Multiple scopes can be selected per trade
    scopeDetails?: string;  // Additional description
  }>;
}

// Permit recommendation
export interface PermitRecommendation {
  permitTypeId: number;
  permitCode: string;
  permitName: string;
  confidence: ConfidenceLevel;
  reason: string;
  required: boolean;        // True = definitely needed, false = recommended
  tradeCode?: string;       // Which trade this permit is for
  userAccepted?: boolean;   // User's acceptance of recommendation
  userOverridden?: boolean; // User explicitly rejected/changed
}

// Step 4: Permits data
export interface WizardPermitsData {
  recommendations: PermitRecommendation[];
  acceptedPermitIds: number[];
  rejectedPermitIds: number[];
  customNotes?: string;
}

// Conditional requirement check
export interface ConditionalRequirement {
  id: string;
  type: 'noc' | 'owner_builder' | 'hvhz' | 'commercial' | 'other';
  name: string;
  description: string;
  triggered: boolean;
  triggerReason?: string;
  userAcknowledged?: boolean;
}

// Step 5: Details data
export interface WizardDetailsData {
  estimatedValue?: number;
  isOwnerBuilder?: boolean;
  conditionalRequirements: ConditionalRequirement[];
  client?: ProjectClient;
  // Commercial-specific
  commercialMetadata?: {
    buildingType?: string;
    occupancyType?: string;
    squareFootage?: number;
    numberOfStories?: number;
  };
}

// Step 6: Review data (summary of all previous steps)
export interface WizardReviewData {
  readyToCreate: boolean;
  blockers: string[];       // Issues preventing creation
  warnings: string[];       // Non-blocking concerns
  jobsToCreate: Array<{
    title: string;
    permitType: string;
    tradeCode: string;
    scopeName?: string;
  }>;
  projectToCreate?: {
    name: string;
    address: string;
    tradeCount: number;
  };
}

// Development project data (for development_project mode)
export interface WizardProjectData {
  developmentType: DevelopmentProjectType | null;
  name: string;
  description: string;
  totalUnits: number;
  unitLabelPrefix: string;   // 'Lot', 'Unit', 'Building', 'Townhome', 'Space'
  unitStartNumber: number;
  // Optional owner info (can also be in details.client)
  ownerName?: string;
  ownerCompany?: string;
  ownerPhone?: string;
  ownerEmail?: string;
}

// Default project data factory
export function createInitialProjectData(): WizardProjectData {
  return {
    developmentType: null,
    name: '',
    description: '',
    totalUnits: 1,
    unitLabelPrefix: 'Lot',
    unitStartNumber: 1,
  };
}

/**
 * Complete wizard state
 */
export interface WizardState {
  // Meta
  id: string;               // Unique wizard session ID
  mode: WizardMode;         // Which flow: single_permit or development_project
  currentStep: WizardStep;
  stepNumber: number;
  totalSteps: number;

  // Progress
  completedSteps: WizardStep[];
  visitedSteps: WizardStep[];

  // Step data - Single permit flow
  basics: WizardBasicsData;
  trades: WizardTradesData;
  scopes: WizardScopesData;
  permits: WizardPermitsData;
  details: WizardDetailsData;
  review: WizardReviewData;

  // Step data - Development project flow
  project: WizardProjectData;

  // Timestamps
  createdAt: string;        // ISO date string
  lastUpdated: string;      // ISO date string

  // Persistence
  savedToFirestore?: boolean;
  firestoreId?: string;     // If synced to Firestore
}

/**
 * Wizard draft for localStorage (minimal for Resume card)
 */
export interface WizardDraft {
  id: string;
  mode: WizardMode;         // Which flow
  projectName?: string;
  address?: string;
  currentStep: number;
  totalSteps: number;
  lastUpdated: string;
  trade?: string;           // Primary trade name for display
  developmentType?: DevelopmentProjectType | null; // For dev projects
}

/**
 * Initial empty wizard state factory
 */
export function createInitialWizardState(): WizardState {
  const id = `wizard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const now = new Date().toISOString();

  return {
    id,
    mode: 'single_permit',     // Default to single permit flow
    currentStep: 'entry',      // Start at entry step
    stepNumber: 1,
    totalSteps: 7,             // Single permit has 7 steps (including entry)
    completedSteps: [],
    visitedSteps: ['entry'],
    basics: {},
    trades: { selectedTradeIds: [], trades: [] },
    scopes: { tradeScopes: [] },
    permits: { recommendations: [], acceptedPermitIds: [], rejectedPermitIds: [] },
    details: { conditionalRequirements: [] },
    review: { readyToCreate: false, blockers: [], warnings: [], jobsToCreate: [] },
    project: createInitialProjectData(),
    createdAt: now,
    lastUpdated: now,
  };
}

/**
 * Convert WizardState to WizardDraft for localStorage
 */
export function wizardStateToDraft(state: WizardState): WizardDraft {
  // For development projects, use project name; for single permits, use basics name
  const projectName = state.mode === 'development_project'
    ? state.project.name || state.basics.projectName
    : state.basics.projectName;

  return {
    id: state.id,
    mode: state.mode,
    projectName,
    address: state.basics.address?.formatted || state.basics.address?.street,
    currentStep: state.stepNumber,
    totalSteps: state.totalSteps,
    lastUpdated: state.lastUpdated,
    trade: state.trades.trades[0]?.name,
    developmentType: state.mode === 'development_project' ? state.project.developmentType : null,
  };
}
